"use client";
import React, { useEffect, useState } from "react";
import { Button } from "@nextui-org/react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { inventoryAPI } from "@/services/api";
import { formatDate } from "@/utils/dateUtils";

interface InventoryItem {
  id: string;
  bookId: string;
  bookName: string;
  quantity: number;
  price: number;
  pricePerBook: number;
  totalCost: number;
  supplierName?: string;
  date?: string;
  createdAt?: string;
  updatedAt?: string;
}

const InventoryView: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchInventory = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);

        const inventoryData = await inventoryAPI.getById(id);


        // Get price and quantity values
        const quantity = Number(inventoryData.quantity) || 0;
        const pricePerBook = Number(inventoryData.price_per_book) || Number(inventoryData.pricePerBook) || 0;
        const price = Number(inventoryData.price) || 0;
        
        // Calculate total cost if not provided (using purchase price, not selling price)
        const totalCost = Number(inventoryData.total_cost) || 
                         Number(inventoryData.totalCost) || 
                         (quantity * price);

        // Process the data to ensure all required fields
        const processedData = {
          id: inventoryData.id,
          bookId: inventoryData.book_id || inventoryData.bookId || "",
          bookName: inventoryData.book_name || inventoryData.bookName || "",
          quantity: quantity,
          price: price,
          pricePerBook: pricePerBook,
          totalCost: totalCost,
          supplierName: inventoryData.supplier_name || inventoryData.supplierName || "N/A",
          date: inventoryData.purchase_date || 
            inventoryData.date ||
                inventoryData.created_at ||
            inventoryData.createdAt ||
                inventoryData.updated_at ||
            inventoryData.updatedAt,
          createdAt: inventoryData.created_at || inventoryData.createdAt,
          updatedAt: inventoryData.updated_at || inventoryData.updatedAt,
        };


        setInventory(processedData);
        setLoading(false);
      } catch (error: any) {
        setError(error.message || "Failed to fetch inventory item");
        setLoading(false);
      }
    };

    fetchInventory();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      const confirmDelete = window.confirm(
        t("Are you sure you want to delete this inventory item?"),
      );

      if (!confirmDelete) return;

      setLoading(true);
      await inventoryAPI.delete(id);
      setLoading(false);

      alert(t("Inventory item deleted successfully!"));
      navigate("/inventory");
    } catch (error: any) {
      alert(
        t("Failed to delete inventory item: ") +
          (error.message || "Unknown error"),
      );
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    try {
      // Make sure we have a valid number
      const validAmount = isNaN(amount) ? 0 : amount;
      
      // Format with two decimal places
      return `${validAmount.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      })} Rs`;
    } catch (e) {
      return `${amount || 0} Rs`;
    }
  };

  if (loading) {
    return (
      <DefaultLayout>
        <div className="flex justify-center items-center h-64">
          <p>{t("Loading...")}</p>
        </div>
      </DefaultLayout>
    );
  }

  if (error) {
    return (
      <DefaultLayout>
        <div className="max-w-lg mx-auto p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {error}
          </div>
          <Button color="primary" onClick={() => navigate("/inventory")}>
            {t("Back to Inventory")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  if (!inventory) {
    return (
      <DefaultLayout>
        <div className="flex justify-center items-center h-64">
          <p>{t("Inventory item not found")}</p>
          <Button
            className="ml-4"
            color="primary"
            onClick={() => navigate("/inventory")}
          >
            {t("Back to Inventory")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <section className="flex flex-col items-center justify-center py-8">
        <div className="max-w-lg w-full p-6 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
            {inventory.bookName}
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Supplier Name")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {inventory.supplierName || "N/A"}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Quantity")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {inventory.quantity}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Price per Book")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {formatCurrency(inventory.pricePerBook)}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Purchase Price")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {formatCurrency(inventory.price)}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Purchase Date")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {formatDate(inventory.date)}
              </span>
            </div>
            <div className="flex flex-col col-span-2 border-b-2 border-gray-300 dark:border-gray-700 p-4">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("Total Cost")}:
              </span>
              <div className="flex items-center justify-center w-full mt-2 p-4 border-2 border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                <span className="text-gray-900 dark:text-gray-200 text-2xl">
                  {formatCurrency(inventory.totalCost)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-6">
            <Button color="primary" onClick={() => navigate(`/inventory`)}>
              {t("Back")}
            </Button>
            <Button
              color="primary"
              onClick={() => navigate(`/inventory/edit/${id}`)}
            >
              {t("Edit Inventory")}
            </Button>
            <Button color="danger" disabled={loading} onClick={handleDelete}>
              {loading ? t("Deleting...") : t("Delete Inventory")}
            </Button>
          </div>
        </div>
      </section>
    </DefaultLayout>
  );
};

export default InventoryView;
